/**
 * TouchInput — on-screen controls for phones and tablets.
 *
 * This module owns two things that are easier to keep together than apart: the raw
 * touch bookkeeping, and the DOM overlay the player's thumbs land on. Splitting them
 * would mean routing every touch through the event bus to reach a widget that sits
 * directly under the finger that produced it.
 *
 * It writes into an existing `Input` instance rather than replacing it, using the
 * same three channels the other devices already use:
 *
 *   - the move stick sets `input.touchStick`, which `moveVector()` treats exactly
 *     like a gamepad's left stick;
 *   - the look drag adds to `input.mouse.dx/dy`, because a drag *is* a displacement
 *     in pixels — the same unit a mouse produces, and therefore the same feel;
 *   - buttons inject a synthetic key code into `input.down`, so `action('reload')`
 *     answers true without gameplay code ever learning that a finger did it.
 *
 * Nothing downstream of `Input` knows this file exists.
 *
 * ## Why the look drag is not a second stick
 *
 * A right stick turns at a *rate*: hold it and you keep turning. That is correct for
 * a physical stick, which springs back to centre, and wrong for a thumb on glass,
 * which does not. Every mobile shooter that feels good uses drag-to-look, because the
 * finger's own position is the memory a stick's spring provides. So the right half of
 * the screen is a trackpad, not a stick.
 *
 * ## Multi-touch is the whole problem
 *
 * Moving, looking and firing happen at once, on three different fingers, and a finger
 * that starts on a button must keep belonging to that button even when it slides off.
 * Every touch is therefore tracked by `identifier` in `_touches` from the moment it
 * lands until it lifts, and its *role* is decided once, on `touchstart`, and never
 * revisited. Deciding per-move is the classic way a look drag steals the fire button
 * mid-burst.
 */

import { applyI18n, onLang } from '../ui/i18n.js';

/** Buttons on the overlay: id, the action they inject, and their label key. */
const BUTTONS = [
  { id: 'fire', action: 'fire', key: 'touch.fire', cls: 'ti-fire' },
  /**
   * The second trigger, on the left.
   *
   * Not a convenience — it is the fix for the one thing a touchscreen shooter has to
   * solve. The right thumb is the look control, and a thumb that is dragging to aim
   * is a thumb that is not on the fire button. With one trigger the player can look
   * *or* shoot, never both, which is exactly the complaint that produced this. Every
   * mobile shooter that works has a trigger under each thumb for this reason: the
   * left one fires while the right one is still aiming.
   */
  { id: 'fire2', action: 'fire', key: 'touch.fire', cls: 'ti-fire2' },
  { id: 'ads', action: 'ads', key: 'touch.ads', cls: 'ti-ads' },
  { id: 'reload', action: 'reload', key: 'touch.reload', cls: 'ti-reload' },
  { id: 'jump', action: 'jump', key: 'touch.jump', cls: 'ti-jump' },
  { id: 'crouch', action: 'crouch', key: 'touch.crouch', cls: 'ti-crouch' },
  { id: 'sprint', action: 'sprint', key: 'touch.sprint', cls: 'ti-sprint' },
  { id: 'swap', action: 'swap', key: 'touch.swap', cls: 'ti-swap' },
  { id: 'grenade', action: 'grenade', key: 'touch.grenade', cls: 'ti-nade' },
  { id: 'tactical', action: 'tactical', key: 'touch.tactical', cls: 'ti-tac' },
  { id: 'melee', action: 'melee', key: 'touch.melee', cls: 'ti-melee' },
  { id: 'pause', action: 'pause', key: 'touch.pause', cls: 'ti-pause' },
];

/** Remembers "I know, let me play in portrait" so the prompt asks at most once. */
const PORTRAIT_OK_KEY = 'ob.portraitOk';

/** Where a moved button lives. Landscape and portrait are stored separately. */
const LAYOUT_KEY = 'ob.touchLayout';

/**
 * Controls that latch instead of needing to be held.
 *
 * Same root cause as the second trigger. Holding aim occupies the thumb that has to
 * be looking, so aiming and turning become mutually exclusive; a latch hands the
 * thumb straight back. Crouch is latched for the same reason and because a crouch
 * you have to hold cannot be combined with anything at all.
 *
 * Fire is deliberately *not* latched — a weapon that keeps firing after the thumb
 * leaves is a weapon that empties itself into a wall.
 */
const TOGGLES = new Set(['ads', 'crouch']);

export const TUNING = {
  /** Radius in px at which the move stick reads fully deflected. */
  stickRadius: 62,
  /**
   * Travel in px before a stick touch counts as movement at all. Small, because the
   * stick is floating — its centre is wherever the thumb landed, so there is no
   * mis-registration to absorb, only genuine tremor.
   */
  stickDeadzone: 8,
  /**
   * Look sensitivity, in the same "radians per pixel" currency as the mouse, before
   * the player's own sensitivity multiplier. Lower than a mouse because a thumb has
   * perhaps a fifth of the travel a mouse hand does, and because the drag is
   * absolute: overshooting cannot be corrected by lifting and re-centring the way a
   * mouse can.
   */
  lookScale: 1.35,
  /**
   * A touch that never moves further than this and lifts within `tapTime` is a tap,
   * not a drag. Used only by the look area, so that tapping the right side of the
   * screen does not nudge the aim.
   */
  tapSlop: 10,
  tapTime: 0.25,
  /**
   * Fire while the look thumb is down, if the player has turned that on. Off by
   * default: it is a real assist, and the player should choose it rather than
   * discover it.
   */
  dragFire: false,
};

export class TouchInput {
  /**
   * @param {import('./Input.js').Input} input the live Input instance to drive
   * @param {HTMLElement} host where the overlay goes; `#ui-root` in practice
   */
  constructor(input, host) {
    this.input = input;
    this.enabled = true;
    this.dragFire = TUNING.dragFire;
    this.editing = false;

    /** identifier -> { role, ... }. Roles are assigned on touchstart and frozen. */
    this._touches = new Map();
    /** Which button ids are currently held, so we can release them on lift. */
    this._held = new Set();
    /** Which latching controls are currently on. */
    this._latched = new Set();

    this._buildOverlay(host);
    this._bind();
  }

  /* ------------------------------- overlay -------------------------------- */

  _buildOverlay(host) {
    const root = document.createElement('div');
    root.className = 'ti-root';
    // The rotate prompt shows and hides on an `(orientation: portrait)` media query,
    // so it stays correct through a rotation without a resize listener that could
    // miss one — but it MUST be dismissible. A phone with rotation lock on never
    // leaves portrait no matter how the player tilts it, and a blocking prompt whose
    // only exit is an action the device refuses to perform is not a prompt, it is a
    // wall. Landscape is a recommendation here, not a requirement: every control is
    // reachable in portrait, it is only tighter.
    root.innerHTML = `
      <div class="ti-stick"><i></i><b></b></div>
      ${BUTTONS.map(
        (b) =>
          `<div class="ti-btn ${b.cls}${
            TOGGLES.has(b.id) ? ' is-toggle' : ''
          }" data-id="${b.id}" data-i18n="${b.key}"></div>`
      ).join('')}
      <div class="ti-rotate">
        <b>⟳</b>
        <span data-i18n="touch.rotate">横向きにすると遊びやすいのじゃ</span>
        <button class="ti-rotate-go" type="button" data-i18n="touch.rotateSkip">
          このまま遊ぶ
        </button>
      </div>
      <div class="ti-editbar">
        <span data-i18n="touch.editHint"></span>
        <button class="ti-edit-reset" type="button" data-i18n="touch.editReset"></button>
        <button class="ti-edit-done" type="button" data-i18n="touch.editDone"></button>
      </div>`;
    (host ?? document.body).appendChild(root);

    this.root = root;
    this.stickEl = root.querySelector('.ti-stick');
    this.stickRing = root.querySelector('.ti-stick i');
    this.stickKnob = root.querySelector('.ti-stick b');

    this.btnEls = new Map();
    for (const b of BUTTONS) {
      this.btnEls.set(b.id, root.querySelector(`[data-id="${b.id}"]`));
    }

    this.editBar = root.querySelector('.ti-editbar');
    applyI18n(root);
    this._fitLabels(root);
    this._offLang = onLang(() => {
      applyI18n(root);
      this._fitLabels(root);
    });

    this.applyLayout();
    // A rotation swaps which stored layout applies, and the browser has usually not
    // finished reporting the new size on the event itself.
    this._onResize = () => setTimeout(() => this.applyLayout(), 60);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onResize);

    this.editBar.addEventListener('pointerup', (e) => {
      const btn = e.target.closest?.('button');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (btn.classList.contains('ti-edit-reset')) this.resetLayout();
      else this.onEditDone?.();
    });

    // The escape hatch. Rotation lock is common and invisible from here — the only
    // signal we would ever get is that the orientation never changes, which is
    // indistinguishable from a player who has not rotated yet.
    const skip = root.querySelector('.ti-rotate-go');
    let stored = false;
    try {
      stored = localStorage.getItem(PORTRAIT_OK_KEY) === '1';
    } catch {
      /* private browsing; ask again, which is the safe direction */
    }
    if (stored) root.classList.add('is-portrait-ok');
    skip?.addEventListener('pointerup', (e) => {
      e.preventDefault();
      e.stopPropagation();
      root.classList.add('is-portrait-ok');
      try {
        localStorage.setItem(PORTRAIT_OK_KEY, '1');
      } catch {
        /* the class alone still gets them into the game this session */
      }
    });
  }

  /* -------------------------------- events -------------------------------- */

  _bind() {
    // `passive: false` on all four: without it the browser will not let us
    // preventDefault, and the page scrolls, rubber-bands and pull-to-refreshes
    // underneath the game. `touchmove` is the one that actually matters, but a
    // scroll can be started by any of them.
    const opts = { passive: false };
    this._onStart = (e) => this._start(e);
    this._onMove = (e) => this._move(e);
    this._onEnd = (e) => this._end(e);

    const el = this.root;
    el.addEventListener('touchstart', this._onStart, opts);
    el.addEventListener('touchmove', this._onMove, opts);
    el.addEventListener('touchend', this._onEnd, opts);
    el.addEventListener('touchcancel', this._onEnd, opts);

    // A touch that lands while the browser is mid-gesture can be cancelled without
    // ever producing touchend. Losing the page is the reliable signal for that.
    this._onHide = () => {
      if (document.visibilityState === 'hidden') this.releaseAll();
    };
    document.addEventListener('visibilitychange', this._onHide);
  }

  /**
   * Which role a new touch takes. Decided once, here, and never re-examined.
   *
   * Buttons win over everything, because they are drawn on top of the look area and
   * the player aimed at them. Otherwise the screen splits down the middle: left
   * thumb moves, right thumb looks.
   */
  _roleFor(target, x) {
    const btn = target?.closest?.('.ti-btn');
    if (btn) return { role: 'button', id: btn.dataset.id, el: btn };
    if (x < window.innerWidth * 0.5) return { role: 'stick' };
    return { role: 'look' };
  }

  _start(e) {
    if (!this.enabled) return;
    e.preventDefault();

    // While editing, a touch on a button grabs it and a touch anywhere else does
    // nothing. No gameplay input is produced at all — see setEditMode.
    if (this.editing) {
      for (const t of e.changedTouches) {
        const el = t.target?.closest?.('.ti-btn');
        if (!el) continue;
        const r = el.getBoundingClientRect();
        this._touches.set(t.identifier, {
          role: 'drag',
          el,
          grabX: t.clientX - r.left,
          grabY: t.clientY - r.top,
        });
        el.classList.add('is-grabbed');
      }
      return;
    }

    // Any touch is "press any key to deploy" — the start screen has no keyboard.
    this.input.engage?.();

    for (const t of e.changedTouches) {
      const info = this._roleFor(t.target, t.clientX);
      const rec = {
        ...info,
        x: t.clientX,
        y: t.clientY,
        ox: t.clientX,
        oy: t.clientY,
        t0: performance.now(),
        moved: 0,
      };
      this._touches.set(t.identifier, rec);

      if (rec.role === 'stick') {
        // Floating origin: the stick centres itself on wherever the thumb landed,
        // so the player never has to find it. A fixed stick on a phone is a stick
        // the player misses, and missing it means walking the wrong way.
        this._showStick(t.clientX, t.clientY);
        this._setStick(0, 0);
      } else if (rec.role === 'button') {
        this._press(rec.id, true);
      } else if (rec.role === 'look' && this.dragFire) {
        this._press('fire', true);
      }
    }
  }

  _move(e) {
    if (!this.enabled) return;
    e.preventDefault();

    for (const t of e.changedTouches) {
      const rec = this._touches.get(t.identifier);
      if (!rec) continue;
      if (rec.role === 'drag') {
        this._dragButton(rec, t.clientX, t.clientY);
        continue;
      }
      const dx = t.clientX - rec.x;
      const dy = t.clientY - rec.y;
      rec.x = t.clientX;
      rec.y = t.clientY;
      rec.moved = Math.max(rec.moved, Math.hypot(t.clientX - rec.ox, t.clientY - rec.oy));

      if (rec.role === 'look') {
        // Straight into the mouse accumulator: the same pixels, drained by the same
        // `consumeMouse()`, so touch look inherits the mouse's sensitivity setting,
        // its ADS scaling and its frame-rate independence for free.
        this.input.mouse.dx += dx * TUNING.lookScale;
        this.input.mouse.dy += dy * TUNING.lookScale;
      } else if (rec.role === 'stick') {
        this._updateStick(rec);
      }
      // A button touch that slides is still that button's touch, and does nothing
      // else. This is the case that a per-move role lookup would get wrong.
    }
  }

  _end(e) {
    e.preventDefault();
    for (const t of e.changedTouches) {
      const rec = this._touches.get(t.identifier);
      if (!rec) continue;
      this._touches.delete(t.identifier);

      if (rec.role === 'drag') {
        rec.el.classList.remove('is-grabbed');
        this._persistLayout();
        continue;
      }
      if (rec.role === 'stick') {
        this._hideStick();
        this._setStick(0, 0);
      } else if (rec.role === 'button') {
        this._press(rec.id, false);
      } else if (rec.role === 'look' && this.dragFire) {
        this._press('fire', false);
      }
    }
  }

  /* --------------------------------- stick -------------------------------- */

  _updateStick(rec) {
    let dx = rec.x - rec.ox;
    let dy = rec.y - rec.oy;
    const len = Math.hypot(dx, dy);

    if (len <= TUNING.stickDeadzone) {
      this._setStick(0, 0);
      this._drawKnob(dx, dy);
      return;
    }

    // Re-centre the origin when the thumb runs past full deflection, so a long drag
    // does not leave the stick pinned and unable to answer a change of direction
    // until the thumb travels all the way back.
    if (len > TUNING.stickRadius) {
      const over = len - TUNING.stickRadius;
      rec.ox += (dx / len) * over;
      rec.oy += (dy / len) * over;
      dx = rec.x - rec.ox;
      dy = rec.y - rec.oy;
      this._showStick(rec.ox, rec.oy);
    }

    const span = TUNING.stickRadius - TUNING.stickDeadzone;
    const mag = Math.min(1, (Math.hypot(dx, dy) - TUNING.stickDeadzone) / span);
    const inv = 1 / (Math.hypot(dx, dy) || 1);
    // `z` is forward-negative to match `moveVector`, and screen y grows downward, so
    // dragging the thumb up (negative dy) has to come out as negative z. They agree
    // without a sign flip; the comment exists because that looks like a bug.
    this._setStick(dx * inv * mag, dy * inv * mag);
    this._drawKnob(dx, dy);
  }

  _setStick(x, z) {
    const s = this.input.touchStick;
    s.x = x;
    s.z = z;
    s.active = x !== 0 || z !== 0;
  }

  _showStick(x, y) {
    this.stickEl.style.transform = `translate(${x}px, ${y}px)`;
    this.stickEl.classList.add('is-on');
  }

  _hideStick() {
    this.stickEl.classList.remove('is-on');
    this._drawKnob(0, 0);
  }

  _drawKnob(dx, dy) {
    const len = Math.hypot(dx, dy);
    const k = len > TUNING.stickRadius ? TUNING.stickRadius / len : 1;
    this.stickKnob.style.transform = `translate(${(dx * k).toFixed(1)}px, ${(dy * k).toFixed(1)}px)`;
  }

  /* -------------------------------- buttons ------------------------------- */

  /**
   * Hold or release a button's action.
   *
   * Goes through `Input.setVirtualAction`, which is the same mechanism the gamepad
   * uses for its face buttons: a synthetic code is added to the held-key set, so
   * `action()`, `actionPressed()` and `actionReleased()` all behave as if it were a
   * key. Fire and aim reach `input.fire` / `input.ads` the same way, via their
   * keyboard bindings.
   */
  _press(id, on) {
    const btn = BUTTONS.find((b) => b.id === id);
    if (!btn) return;
    const el = this.btnEls.get(id);

    if (TOGGLES.has(id)) {
      // Latching: the release is ignored entirely and the press flips the state.
      if (!on) return;
      const next = !this._latched.has(id);
      if (next) this._latched.add(id);
      else this._latched.delete(id);
      el?.classList.toggle('is-down', next);
      this.input.setVirtualAction(`Touch_${id}`, btn.action, next);
      return;
    }

    if (el) el.classList.toggle('is-down', on);
    if (on) this._held.add(id);
    else this._held.delete(id);
    this.input.setVirtualAction(`Touch_${id}`, btn.action, on);
  }

  /** Drop every held control. Called when the page is hidden or the overlay hides. */
  releaseAll() {
    for (const id of [...this._held]) this._press(id, false);
    // Latches are cleared explicitly: `_press(id, false)` is a no-op for them by
    // design, so the loop above cannot let go of a latched aim.
    for (const id of [...this._latched]) {
      const btn = BUTTONS.find((b) => b.id === id);
      this.btnEls.get(id)?.classList.remove('is-down');
      if (btn) this.input.setVirtualAction(`Touch_${id}`, btn.action, false);
    }
    this._latched.clear();
    this._touches.clear();
    this._hideStick();
    this._setStick(0, 0);
  }

  /* ----------------------------- custom layout ---------------------------- */

  /**
   * Landscape and portrait get separate layouts.
   *
   * They have to: a position that is under the thumb on a wide frame is in the middle
   * of the screen on a tall one, so one shared layout would be wrong in whichever
   * orientation the player did not arrange it in.
   */
  _layoutKey() {
    return window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
  }

  _loadLayouts() {
    try {
      return JSON.parse(localStorage.getItem(LAYOUT_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }

  _saveLayouts(all) {
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(all));
    } catch {
      /* private browsing: the layout still applies for this session */
    }
  }

  /**
   * Apply the saved layout for the current orientation.
   *
   * Stored as percentages of the viewport rather than pixels, so a layout arranged on
   * one phone is still roughly right after a rotation or on a different screen. The
   * inline `left`/`top` must also clear `right`/`bottom`, because the stylesheet
   * anchors these buttons to the far edges and a half-overridden box lands in a
   * position neither rule intended.
   */
  applyLayout() {
    const saved = this._loadLayouts()[this._layoutKey()] || {};
    for (const [id, el] of this.btnEls) {
      const p = saved[id];
      if (p) {
        el.style.left = `${p.x}%`;
        el.style.top = `${p.y}%`;
        el.style.right = 'auto';
        el.style.bottom = 'auto';
      } else {
        el.style.left = el.style.top = el.style.right = el.style.bottom = '';
      }
    }
  }

  /** Forget the custom layout for this orientation and go back to the stylesheet. */
  resetLayout() {
    const all = this._loadLayouts();
    delete all[this._layoutKey()];
    this._saveLayouts(all);
    this.applyLayout();
  }

  /**
   * Enter or leave layout editing.
   *
   * While editing, every touch drags a button and nothing reaches the game — which is
   * why the game is paused around it by the caller. Leaving persists whatever the
   * buttons ended up at.
   */
  setEditMode(on) {
    this.editing = !!on;
    this.releaseAll();
    this.root.classList.toggle('is-editing', this.editing);
    this.editBar.classList.toggle('is-on', this.editing);
  }

  /** Write every button's current on-screen position into storage, as percentages. */
  _persistLayout() {
    const all = this._loadLayouts();
    const key = this._layoutKey();
    const out = {};
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    for (const [id, el] of this.btnEls) {
      const r = el.getBoundingClientRect();
      out[id] = { x: +((r.left / vw) * 100).toFixed(2), y: +((r.top / vh) * 100).toFixed(2) };
    }
    all[key] = out;
    this._saveLayouts(all);
  }

  /** Move one button during a drag, clamped so it can never leave the screen. */
  _dragButton(rec, x, y) {
    const el = rec.el;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const left = Math.max(0, Math.min(window.innerWidth - w, x - rec.grabX));
    const top = Math.max(0, Math.min(window.innerHeight - h, y - rec.grabY));
    el.style.left = `${((left / window.innerWidth) * 100).toFixed(2)}%`;
    el.style.top = `${((top / window.innerHeight) * 100).toFixed(2)}%`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }

  /* ------------------------------- lifecycle ------------------------------ */

  /** Hide the controls without destroying them — for menus, which are tapped directly. */
  /**
   * Shrink the type on buttons whose label does not fit.
   *
   * The Japanese labels used to be two characters — 投擲, 戦術, 装填 — and each sat
   * comfortably inside a 56 px circle. They are now the katakana a player actually
   * recognises (グレネード, タクティカル, リロード), which is up to five, and at the
   * authored size they wrapped mid-word: `メニ` above `ュー`. A small kana broken
   * across two lines reads worse than the English it replaced.
   *
   * Measured off the label rather than hard-coded per button, so it holds for
   * whatever a future language decides to call these.
   */
  _fitLabels(root) {
    for (const el of root.querySelectorAll('.ti-btn')) {
      const n = (el.textContent ?? '').trim().length;
      el.classList.toggle('is-long', n >= 4);
      el.classList.toggle('is-xlong', n >= 6);
    }
  }

  setVisible(on) {
    // Editing is the one time the controls must stay up over a paused game: the whole
    // point is to arrange them against the screen they will be used on. The state
    // machine that drives this has no idea that is happening, so the veto lives here.
    if (this.editing) return;
    this.enabled = !!on;
    this.root.classList.toggle('is-hidden', !on);
    if (!on) this.releaseAll();
  }

  /** Show the controls for editing, whatever the game state says. */
  beginEdit() {
    this.enabled = true;
    this.root.classList.remove('is-hidden');
    this.setEditMode(true);
  }

  endEdit() {
    this.setEditMode(false);
  }

  dispose() {
    this.releaseAll();
    this._offLang?.();
    document.removeEventListener('visibilitychange', this._onHide);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('orientationchange', this._onResize);
    this.root.remove();
  }
}
